import { useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  FileText,
  FolderOpen,
  GitBranch,
  Headset,
  MessageSquare,
  UserRound,
  Wallet,
} from "lucide-react";
import { BENEFIT_GROUPS, FUNCTIONS } from "../../constants/collect";
import { CornerMarks, SectionShell, useFade } from "./ui";

const ICONS = {
  folder: FolderOpen,
  user: UserRound,
  file: FileText,
  flow: GitBranch,
  headset: Headset,
  message: MessageSquare,
  wallet: Wallet,
  chart: BarChart3,
};

function CollectOps() {
  const { fade } = useFade();
  const [activeId, setActiveId] = useState(FUNCTIONS[0].id);
  const active = FUNCTIONS.find((item) => item.id === activeId) || FUNCTIONS[0];
  const Icon = ICONS[active.icon] || FolderOpen;

  return (
    <>
      <SectionShell id="functions" labelledBy="functions-heading" tone="paper">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Core functionalities
            </p>
            <h2
              id="functions-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Eight desks.
              <span className="mt-1 block">One recovery file.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Open a docket to see how SimCollect runs the recovery lifecycle — from portfolio
              onboarding to executive reporting — without a black stage or a filmstrip.
            </p>
          </motion.header>

          <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)]">
            <ol className="flex h-full flex-col border border-navy/10 bg-white">
              {FUNCTIONS.map((item) => {
                const current = item.id === active.id;
                return (
                  <li key={item.id} className="flex min-h-0 flex-1 border-t border-navy/8 first:border-t-0">
                    <button
                      type="button"
                      onClick={() => setActiveId(item.id)}
                      className={`flex h-full min-h-16 w-full cursor-pointer items-center gap-4 px-4 py-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-collect-gold ${
                        current
                          ? "border-l-[3px] border-collect-red bg-[#fff4f4]"
                          : "border-l-[3px] border-transparent hover:bg-[#faf6f4]"
                      }`}
                      aria-pressed={current}
                    >
                      <span
                        className={`font-mono text-[11px] tracking-[0.16em] ${current ? "text-collect-red" : "text-navy/35"}`}
                      >
                        {item.index}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold tracking-tight text-navy">
                          {item.short}
                        </span>
                        <span className="mt-0.5 hidden text-xs text-navy/50 sm:block">{item.kicker}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="relative flex h-full min-h-[28rem] flex-col border border-navy/10 bg-white p-6 sm:p-8">
              <CornerMarks />
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.2em] text-collect-red">
                    FILE {active.index} · {active.short.toUpperCase()}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold tracking-tight text-navy sm:text-3xl">
                    {active.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-base leading-relaxed text-navy/60">{active.copy}</p>
                </div>
                <span className="hidden h-12 w-12 shrink-0 items-center justify-center border border-collect-gold/40 bg-[#faf6f4] text-collect-red sm:inline-flex">
                  <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                </span>
              </div>

              <ul className="mt-8 grid flex-1 auto-rows-fr content-stretch gap-2 sm:grid-cols-2">
                {active.items.map((item) => (
                  <li
                    key={item}
                    className="flex h-full items-start gap-3 border-l-[3px] border-collect-gold bg-[#faf6f4] px-3 py-3 text-sm font-semibold leading-snug text-navy"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-collect-red" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </SectionShell>

      <SectionShell id="benefits" labelledBy="benefits-heading" tone="white">
        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Features &amp; benefits
            </p>
            <h2
              id="benefits-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Value for every desk
              <span className="mt-1 block">in the recovery room</span>
            </h2>
          </motion.header>

          <ul className="mt-12 grid items-stretch gap-5 lg:grid-cols-2">
            {BENEFIT_GROUPS.filter((group) => group.layout === "wide").map((group) => (
              <li key={group.id} className="min-h-0">
                <BenefitCard group={group} />
              </li>
            ))}
          </ul>

          <div className="my-10 flex items-center gap-4" aria-hidden="true">
            <span className="h-px flex-1 bg-navy/10" />
            <span className="flex items-center gap-3">
              <span className="h-px w-8 bg-collect-gold" />
              <span className="h-2 w-2 rotate-45 bg-collect-red" />
              <span className="h-px w-8 bg-collect-gold" />
            </span>
            <span className="h-px flex-1 bg-navy/10" />
          </div>

          <ul className="grid items-stretch gap-5 lg:grid-cols-3">
            {BENEFIT_GROUPS.filter((group) => group.layout !== "wide").map((group) => (
              <li key={group.id} className="min-h-0">
                <BenefitCard group={group} />
              </li>
            ))}
          </ul>
        </div>
      </SectionShell>
    </>
  );
}

export default CollectOps;

function BenefitCard({ group }) {
  const wide = group.layout === "wide";

  return (
    <article
      className={`flex h-full overflow-hidden border border-navy/10 bg-white ${
        wide ? "flex-col sm:flex-row" : "flex-col"
      }`}
    >
      <figure
        className={`relative overflow-hidden bg-[#faf6f4] ${
          wide ? "min-h-[16rem] sm:w-[44%] sm:min-h-full sm:shrink-0" : "aspect-[16/10]"
        }`}
      >
        <BenefitArt src={group.art} label={group.title} spec={group.spec} />
        {wide ? null : (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-navy/55 to-transparent" />
            <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4">
              <span className="font-mono text-[11px] tracking-[0.2em] text-collect-gold">{group.index}</span>
              <span className="max-w-[85%] text-right text-sm font-semibold tracking-tight text-white">
                {group.title}
              </span>
            </figcaption>
          </>
        )}
      </figure>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[#faf6f4] p-5 sm:p-6">
        {wide ? (
          <>
            <p className="font-mono text-[11px] tracking-[0.2em] text-collect-red">{group.index}</p>
            <h3 className="mt-2 text-xl font-semibold tracking-tight text-navy">{group.title}</h3>
          </>
        ) : (
          <h3 className="sr-only">{group.title}</h3>
        )}
        {wide ? (
          <ul className="mt-5 flex-1 space-y-2.5">
            {group.items.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm leading-snug text-navy/80">
                <span className="mt-1.5 flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full bg-collect-gold/25 ring-1 ring-collect-gold">
                  <span className="h-1.5 w-1.5 rounded-full bg-collect-red" />
                </span>
                <span className="font-semibold tracking-tight">{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="grid flex-1 auto-rows-fr grid-cols-1 gap-2 sm:grid-cols-2">
            {group.items.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2.5 border-l-[3px] border-collect-gold bg-white px-3 py-2.5 text-sm font-semibold leading-snug text-navy/80"
              >
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

function BenefitArt({ src, label, spec }) {
  const [failed, setFailed] = useState(!src);

  if (failed) {
    return (
      <div className="flex h-full min-h-[11rem] flex-col justify-between gap-3 p-4" role="img" aria-label={spec}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/35">
          {label} · illustrator
        </p>
        <p className="text-xs leading-relaxed text-navy/45">{spec}</p>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      decoding="async"
      className="absolute inset-0 h-full w-full object-cover object-center"
      onError={() => setFailed(true)}
    />
  );
}
